import { DynamicTerraceHub } from './DynamicTerraceHub';
import { DynamicProfileStoreBase } from '../DynamicProfileStoreBase';
import { ProfileKeyCode, SketchState } from '../DynamicProfileEnums';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { reachLineSymbol, channelLineSymbol, reachHighlightSymbol, channelStationSymbol, reachStationSymbol, trackerSymbol } from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';
import { TerraceDesignToolsController } from 'cd-eft/services/eftSetApi';
import { XyzPoint } from 'cd-eft/models/SurveyEngineering/XyzPoint';
import { parseStationString } from 'cd-eft/utils/stationUtilities';
import {
  esriGeometryEngine,
  Graphic,
  GraphicsLayer,
  Point,
  Polyline,
  SketchViewModel,
  MapView,
  FeatureLayer,
} from 'cd-eft/utils/esriTypes';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import { DynamicTerraceTableStore } from './DynamicTerraceTableStore';
import { TerraceUiState } from '../../TerraceDesignStore';
import { AppConstants } from 'cd-eft/appConstants';

const { designStore, terraceDesignStore } = stores.AppStore;

const ERROR_SELF_CROSSING = 'Common_29';

class RubberbandSketchViewModel {
  private _rubberbandSketchLayer: GraphicsLayer | undefined = void 0;
  private startPoint: Point | undefined = void 0;
  public _profileView: MapView | undefined = void 0;
  private viewPointerMoveHandle;
  private viewImmediateClickHandle;

  constructor(mapView: MapView, sketchLayer: GraphicsLayer) {
    this._profileView = mapView;
    this._rubberbandSketchLayer = sketchLayer;
  }

  public create(startPoint?: Point) {
    this.startPoint = startPoint;
    this.viewImmediateClickHandle = this._profileView!.on('immediate-click', this.viewImmediateClickHandler);
    this.viewPointerMoveHandle = this._profileView!.on('pointer-move', this.viewPointerMoveHandler);
  }

  public complete() {
    this.viewPointerMoveHandle?.remove();
    this.viewImmediateClickHandle?.remove();
    this.rubberbandSketchLayer.removeAll();
  }

  protected get rubberbandSketchLayer() {
    return this._rubberbandSketchLayer!;
  }

  private viewPointerMoveHandler = async (event: __esri.ViewPointerMoveEvent) => {
    this.rubberbandSketchLayer.removeAll();
    const movingPoint = this._profileView?.toMap(event);

    if (this.startPoint && movingPoint) {
      const rubberBandLine = new Polyline({ spatialReference: this._profileView!.spatialReference, hasZ: true });
      rubberBandLine.addPath([this.startPoint, movingPoint]);

      const rubberBandGraphic = new Graphic({ geometry: rubberBandLine, symbol: reachLineSymbol.designLineSymbol });
      this.rubberbandSketchLayer.add(rubberBandGraphic);
    }
  };

  private viewImmediateClickHandler = async (event: __esri.ViewImmediateClickEvent) => {
    this.complete();
  };
}
class DynamicTerraceProfileStore extends DynamicProfileStoreBase {
  public hub: DynamicTerraceHub;
  public alignmentCenterLine = [] as number[][];
  private reachVertices = [] as number[][];
  private isAfterReshapeVertexAdd = false;
  private addTerraceEdits: any;
  private rubberbandSketchVm: RubberbandSketchViewModel | undefined;
  private extendSketchVM: SketchViewModel | undefined = void 0;

  /** Add Drain, Add Block, or Match Vertex setup **/
  private terraceProfileClickHandler;
  private terraceProfileMoveHandler;
  private _profileTrackLayer: GraphicsLayer | undefined = void 0;
  private channelIndexes: any[] = [];
  private centerline: any;

  private viewPointerMoveHandle;
  private viewPointerLeaveHandle
  private viewPointerNotInitialized: boolean = true;
  /**
   *
   */
  constructor(hub: DynamicTerraceHub) {
    super();

    this.hub = hub;
    this.profileKeys = [ProfileKeyCode.TGS, ProfileKeyCode.REACH];
  }

  // Abstract methods
  async createChannelFromSavedUgo(): Promise<void> { }
  async updateUgoTableOnChangeStationValue(): Promise<void> { }
  protected setUpTerraceAlignmentView(event: any): void { }
  async redrawUgoProfiles(): Promise<void> { }

  protected initSketchVm(): void {
    if (this.mainSketchVm && this.sketchLayer) {
      this.mainSketchVm.polylineSymbol = channelLineSymbol.designLineSymbol;
      this.mainSketchVm.view = this._profileView!;
      this.mainSketchVm.layer = this.sketchLayer;
      this.mainSketchVm.updateOnGraphicClick = false;
      this.mainSketchVm.defaultUpdateOptions = {
        toggleToolOnClick: false,
      };
    }

    this.initializeViewPointer();
  }

  private initializeViewPointer() {
    if (this.viewPointerNotInitialized) {
      this.viewPointerMoveHandle = this._profileView!.on('pointer-move', (async (e: __esri.ViewPointerMoveEvent) => {
        const point = this._profileView?.toMap({ x: e.x, y: e.y });
        if (point && this.mapAlignmentSet) {
          this.hub.MapStore?.setTracker(
            point.x / (designStore.commonHorizontalStretchFactor || 1),
            point.y / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor)
          );
        }
        if (this.stationHitTestOptions) {
          const result = await this._profileView?.hitTest(e, this.stationHitTestOptions);
          if (result?.results[0]) {
            const stationStation = result?.results[0].graphic.attributes.Station;
            if (stationStation) {
              const stationIndex = terraceDesignStore.terraceDesignObject.terraceDesignStations.findIndex(s =>
                s.endStation === stationStation);
              // if (stationIndex === 0) {
              //   stationIndex = 1;
              // }
              if ((stationIndex > -1) && (stationIndex < terraceDesignStore.terraceDesignObject.terraceDesignStations.length)) {
                this.trackStation(terraceDesignStore.terraceDesignObject.terraceDesignStations[stationIndex]);
                if (this.hub.TableStore) {
                  (this.hub.TableStore as DynamicTerraceTableStore).highlightRows(stationIndex);
                }
              }
            }
          } else {
            this.unTrackUnselectedProfilePoints();
            if (this.hub.TableStore) {
              (this.hub.TableStore as DynamicTerraceTableStore).clearHighlights();
            }
          }
        }
      }));

      this.viewPointerLeaveHandle = this._profileView!.on('pointer-leave', (e: __esri.ViewPointerMoveEvent) => {
        this.hub.MapStore?.clearTracker();
      });

      this.viewPointerNotInitialized = false;
    }
  }

  updateGridValues(gridValue: number, updateLines: boolean = true) {
    this.updateUserGridValues(gridValue, updateLines);
  }

  sketchPolyline() {
    this.clearDelete();
    this.removeSketch();
    this.removeMidPointStationLayer();
    this.broadcastSketchState({ state: SketchState.START, keyCode: this.profileKeys[1] });
    this.mainSketchVm?.create('polyline');
    this.setSketchLayer();
  }

  reshapePolyline(propAddEdits?: any) {
    this.addTerraceEdits = propAddEdits;
    this.generateReachStationLayer(true);
    if (this.mainSketchVm?.layer && this.mainSketchVm.layer.graphics && this.mainSketchVm.layer.graphics.length > 0) {
      this.mainSketchVm?.update([this.mainSketchVm.layer.graphics.getItemAt(0)], {
        tool: 'reshape',
        enableRotation: false,
        enableScaling: false,
        preserveAspectRatio: false,
        toggleToolOnClick: false,
      });
      this.isAfterReshapeVertexAdd = false;
    }
  }

  public removeSketch(): void {
    // this.unTrackProfilePoints();
    // this.clearDelete();
    this.removeReachStationLayer();
    // this.removeMidPointStationLayer();
    this.sketchLayer?.graphics.removeAll();
    // this.reachVertices.length = 0;
    // designStore.reachStationsLayerExists = false;
    // this.clearCrossSectionWaterwayStations();
    this.hub.MapStore?.clearReachStations();
  }

  protected async handlePolylineCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    if (event.state === 'start' || event.state === 'active') {
      if (event.toolEventInfo.type === 'vertex-add') {
        let x = event.toolEventInfo.vertices[0].coordinates[0];
        x = designStore.commonHorizontalStretchFactor ? x / designStore.commonHorizontalStretchFactor : x;
        const y = event.toolEventInfo.vertices[0].coordinates[1];

        if (this._isValidLine) {
          const line = event.graphic.geometry as Polyline;
          if (this.isValidLastSegment(line)) {
            this.clearInvalidState(event.graphic);
            this.reachVertices.push([x, y]);
          } else {
            event.graphic.symbol = channelLineSymbol.invalidLineSymbol;
            MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);
          }
        } else {
          // This is for the case of "slow" twice clicks
          this.mainSketchVm?.complete();
          const length = (event.graphic.geometry as Polyline).paths[0].length;
          await this.deleteReachVertex({ index: length - 1 });
          event.graphic.symbol = channelLineSymbol.designLineSymbol;
          this._isValidLine = true;
        }
      } else if (event.toolEventInfo.type === 'cursor-update') {
        if (!this._isValidLine) {
          this.mainSketchVm?.undo();
          this.clearInvalidState(event.graphic, 1500);
        }
      }
    }

    if (event.state === 'complete') {
      if (!this._isValidLine) {
        const length = (event.graphic.geometry as Polyline).paths[0].length;
        await this.deleteReachVertex({ index: length - 1 });
        this.clearInvalidState(void 0, 1500);
      }
      const isSuccess = await this.createDefaultTerraceChannel(this.reachVertices);
      if (isSuccess) {
        terraceDesignStore.summaryInfo.channelLength = 1;
        await terraceDesignStore.handleUiStateUpdate({ uiStateType: TerraceUiState.ChannelDesignSave.string });
      }
      this.broadcastSketchState({ state: SketchState.COMPLETE, keyCode: this.profileKeys[1] });
      this.reachVertices.length = 0;
    }

    if (event.state === 'cancel') {
      this.broadcastSketchState({ state: SketchState.CANCEL, keyCode: this.profileKeys[1] });
      this.reachVertices.length = 0;
    }
  }

  async createDefaultTerraceChannel(channelData?) {
    designStore.setLoader('Generating channel reaches...');
    let isSuccess = false;
    const stationVertices = {};
    channelData.forEach((point) => {
      stationVertices[point[0]] = point[1] / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);
    });

    try {
      const response = (await TerraceDesignToolsController.createTerraceDefaultReachesUsingPOST({ alignedElementId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId, stationVertices: stationVertices })).data as any;
      if (response) {
        // Populate table
        this.broadcastNewVertexInfo(response.terraceVertexDTOList);

        // Populate secondary profiles
        if (response.constructedTerrace) {
          terraceDesignStore.constructedProfileStations = response.constructedTerrace.stationDTOList;
        }
        if (response.settledTerrace) {
          terraceDesignStore.settledProfileStations = response.settledTerrace.stationDTOList;
        }
        if (response.waterLevel) {
          terraceDesignStore.waterLevelData = response.waterLevel.stationDTOList;
        }
        this.waterLevelLayer?.removeAll();
        this.constructedTerraceLayer?.removeAll();
        this.settledTerraceLayer?.removeAll();
        designStore.setTerraceSecLayerVisibility = true;
        await this.redrawMappingProfiles();
        await this.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
        isSuccess = true;
      }
    } catch (error) {
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage, [], (error as any).response.data.originalErrorCode);
      }
    } finally {
      designStore.setLoader('');
    }
    return isSuccess
  }

  async handleReshapeEvent(event: __esri.SketchViewModelUpdateEvent): Promise<void> {
    if (event.toolEventInfo) {
      if (event.toolEventInfo.type.includes('reshape-stop')) {
        const line = event.graphics[0].geometry as Polyline;
        if (this.isValidFromStart(line)) {
          this.clearInvalidState(event.graphics[0]);

          const toolEventInfo = event.toolEventInfo as __esri.MoveEventInfo;
          if (toolEventInfo) {
            const moverAttr = toolEventInfo.mover.attributes;
            const movedPoint: Point = toolEventInfo.mover.geometry as Point;

            //get profile x and y values
            const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
            const yScale = designStore.commonVerticalStretchFactor ? designStore.commonVerticalStretchFactor : AppConstants.profileStretchFactor;
            const dataX = (movedPoint as Point).x / xScale;
            const dataY = (movedPoint as Point).y / yScale;
            const newVertex: XyzPoint = new XyzPoint(dataX, dataY, 0);

            if (moverAttr.pointIndex >= 0) {
              this.broadcastMoveVertexInfo(newVertex, moverAttr.pointIndex, this.addTerraceEdits);
            }
          }
        } else {
          event.graphics[0].symbol = reachLineSymbol.invalidLineSymbol;
          await MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);


          // cleare error state
          setTimeout(() => {
            this.mainSketchVm?.undo();
            event.graphics[0].symbol = reachLineSymbol.designLineSymbol;
            this._isValidLine = true;
          }, 500);

          setTimeout(() => {
            MessageCenter.clearMessages();
          }, 1500);
        }
      }
      else if (event.toolEventInfo.type.includes('move-start')) {
      } else if (event.toolEventInfo.type.includes('move')) {
        this.mainSketchVm?.undo();
      } else if (event.toolEventInfo.type.includes('vertex-remove')) {
        this.mainSketchVm?.undo();
      } else if (event.toolEventInfo.type.includes('vertex-add')) {
        this.mainSketchVm?.undo();
        this.isAfterReshapeVertexAdd = true;
      } else if (event.toolEventInfo.type.includes('reshape-start')) {
      } else if (event.toolEventInfo.type.includes('reshape')) {
        if (this.isAfterReshapeVertexAdd) {
          this.mainSketchVm?.undo();
          this.isAfterReshapeVertexAdd = false;
        }
      }
    }
  }

  async editTerrace(info: any) {
    if (this.sketchLayer?.graphics && this.sketchLayer.graphics.length > 0) {
      const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
      const yScale = designStore.commonVerticalStretchFactor ? designStore.commonVerticalStretchFactor : AppConstants.profileStretchFactor;
      const currentLine = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
      const formtStation = parseStationString(info.point[0]) * xScale;
      if (formtStation !== undefined) {
        currentLine.setPoint(0, (info.index), [formtStation, info.point[1]]);

        //get profile x and y values
        const dataX = formtStation / xScale;
        const dataY = info.point[1] / yScale;
        const newVertex: XyzPoint = new XyzPoint(dataX, dataY, 0);
        if (info.index >= 0) {
          await this.updateTableOnChangeStationValue(newVertex, (info.index));
          await this.redrawMappingProfiles();
          await this.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
        }
      }
    }
  }

  async handlePointCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    if (event.state === 'complete' && event.graphic && event.graphic.geometry) {
      const insertPoint = event.graphic.geometry as Point;
      const sr = event.graphic.geometry.spatialReference;

      if (this.sketchLayer?.graphics && this.sketchLayer.graphics.length > 1) {
        const terrace = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
        // Check if the polyline contains the point
        if (!esriGeometryEngine.contains(terrace, insertPoint)) {
          // If the polyline doesn't contain the point, display the error message
          await MessageCenter.addErrorFromCode('WDT_104');
        }
        const path = terrace?.paths[0];

        const [startPoint, stopPoint] = this.getStartAndStopPoint(terrace);

        if (esriGeometryEngine.distance(insertPoint, startPoint) === 0) {
          this.mainSketchVm?.create('point');
          this.rubberbandSketchVm?.create(insertPoint);
        } else if (esriGeometryEngine.distance(insertPoint, stopPoint) === 0) {
          this.extendSketchVM?.create('point');
          this.rubberbandSketchVm?.create(insertPoint);
        } else if (this.isSnappedOnVertex(insertPoint, terrace)) {
          // do nothing
        } else {
          if (path) {
            let pointIndex = -1;
            let beginPoint = path[0];
            for (let index = 1; index < path.length; index++) {
              const endPoint = path[index];
              const polyline = new Polyline({ paths: [[beginPoint, endPoint]], spatialReference: sr });
              if (esriGeometryEngine.intersects(polyline, insertPoint)) {
                pointIndex = index;
                break;
              }
              beginPoint = endPoint;
            }
            // prepare for profile view
            const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
            const dataX = (insertPoint as Point).x / xScale;
            const dataY = (insertPoint as Point).y / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);
            const newVertex: XyzPoint = new XyzPoint(dataX, dataY, 0);
            if (pointIndex >= 0) {

              if (path[0][0] > path[path.length - 1][0]) {
                //digitized right to left, correct the order index for row.
                const rowIndex = (path.length) - pointIndex;
                terrace.insertPoint(0, pointIndex, insertPoint);
                this.broadcastInsertVertexInfo(newVertex, rowIndex, this.profileKeys[0], this.addTerraceEdits);
              }
              else {
                terrace.insertPoint(0, pointIndex, insertPoint);
                this.broadcastInsertVertexInfo(newVertex, pointIndex, this.profileKeys[0], this.addTerraceEdits);
              }
            }
          }
        }
      }

      if (this.mainSketchVm) this.mainSketchVm!.snappingOptions = {} as any;
      this.sketchLayer?.graphics.pop();
    } else if (event.state === "complete" || event.state === "cancel") {
      this.changePointSymbol('reachStation');
      if (this.mainSketchVm) this.mainSketchVm!.snappingOptions = {} as any;
    }
  }
  checkAboveCenterline(x: number, y: number): boolean {
    let checkAbove = false;
    let found = false;
    let resultY;
    const line: Polyline = this._groundSurfaceProfileline as Polyline;
    for (let i = 0; i <= line.paths.length - 1; i++) {
      const path = line.paths[i];
      for (let j = 0; j < path.length - 1; j++) {
        const start = path[j];
        const end = path[j + 1];

        if ((x >= start[0] && x <= end[0]) || (x >= end[0] && x <= start[0])) {
          resultY = start[1] + ((x - start[0]) * (end[1] - start[1])) / (end[0] - start[0]);
          found = true;
          break;
        }
      }

      if (found) {
        break;
      }
    }

    if (found && y > resultY) {
      checkAbove = true;
    }

    return checkAbove;
  }

  verifyDistanceWithinLimit(x: number, y: number, distance: number): boolean {
    let isDistanceExceeded = false;
    let found = false;
    let resultY;
    const line: Polyline = this._groundSurfaceProfileline as Polyline;
    for (let i = 0; i <= line.paths.length - 1; i++) {
      const path = line.paths[i];
      for (let j = 0; j < path.length - 1; j++) {
        const start = path[j];
        const end = path[j + 1];

        if ((x >= start[0] && x <= end[0]) || (x >= end[0] && x <= start[0])) {
          resultY = start[1] + ((x - start[0]) * (end[1] - start[1])) / (end[0] - start[0]);
          found = true;
          break;
        }
      }

      if (found) {
        break;
      }
    }

    if (found && y < resultY - distance * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor)) {
      isDistanceExceeded = true;
    }

    return isDistanceExceeded;
  }

  async addReachVertex(info: any) {
    this.mainSketchVm?.complete();
    this.changePointSymbol('reachStation');
    if (this.sketchLayer && this.sketchLayer.graphics && this.sketchLayer.graphics.length > 0) {
      const currentLine = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
      const path = currentLine?.paths[0];
      // Create the new point

      const newPoint = new Point({
        x: parseFloat(info.item.endStation.replace('+', '')) * (designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1),
        y: parseFloat(info.item.channelElev) * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor),
        spatialReference: currentLine.spatialReference
      });

      if (path[0][0] > path[path.length - 1][0]) {
        //digitized right to left, correct the order of pointIndex
        info.index = (path.length) - info.index;
      }

      // Add the new point to the polyline at the specified index
      currentLine.insertPoint(0, info.index, newPoint);
      const newLine = new Polyline({
        paths: currentLine.paths,
        spatialReference: currentLine.spatialReference,
      });
      this.sketchLayer.graphics.getItemAt(0).geometry = newLine;
    }
    await this.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId)
    const reachStationsLayer = this._profileView?.map.findLayerById('deleteReachStations') as FeatureLayer;
    if (reachStationsLayer) {
      this.addPoint();
    }
  }


  SketchPoint(propAddEdits?: any) {
    this.addTerraceEdits = propAddEdits;
    this.clearDelete();
    if (this.mainSketchVm) {
      this.mainSketchVm!.snappingOptions = {
        distance: 21,
        enabled: true,
        featureSources: [{ layer: this.sketchLayer }] as any,
      } as any;
    }

    this.mainSketchVm?.create('point');
    this.generateReachStationLayer(true);
    this.changePointSymbol('editableReachStation');

  }

  async stopEditing() {
    this.clearDelete();
    this.stopTerraceSketch();
    this.mainSketchVm?.complete();
    await this.createReachFromSavedDesign(terraceDesignStore.terraceDesignObject.terraceDesignStations, terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId);
  }

  startProfileEditTool(addFunction, type: string) {
    this.stopTerraceSketch();
    if (this._profileView) {
      this.setUpMultipoint(type);

      if (this.channelIndexes.length > 0) {   // Only add listeners if there are editable stations
        const element = document.getElementById('terrace-Profile');
        if (element) element.style.cursor = 'pointer';

        // Add listeners
        document.addEventListener('keydown', this.handleTerraceEscKey);
        this.terraceProfileMoveHandler = this._profileView.on('pointer-move', (event) => this.handleProfileMove(event));
        this.terraceProfileClickHandler = this._profileView.on('pointer-down', (event) => {
          const point = this._profileView!.toMap(event);
          this.handleTerraceProfileClick(point, addFunction);
        });
      }
    }
  }

  private stopTerraceSketch() {
    const element = document.getElementById('terrace-Profile');
    if (element) element.style.cursor = 'default';
    this.channelIndexes = [];
    this.feedbackLayer.removeAll();
    if (this._profileView) {
      const editStationsLyr = this._profileView.map.findLayerById('editableStations');
      this._profileView.map.removeMany([editStationsLyr, this.feedbackLayer]);
    }
    this.terraceProfileClickHandler?.remove();
    this.terraceProfileClickHandler = undefined;
    this.terraceProfileMoveHandler?.remove();
    this.terraceProfileMoveHandler = undefined;
    document.removeEventListener('keydown', this.handleTerraceEscKey);
  }

  private handleTerraceProfileClick(point: Point, addFunction) {
    const findResult = esriGeometryEngine.nearestVertex(this.channelStationMP, point);
    if (findResult) {
      const x = findResult.coordinate.x;
      const y = findResult.coordinate.y;
      const index = this.channelIndexes.find((obj) => obj.point.x === x && obj.point.y === y).index;
      const station = terraceDesignStore.terraceDesignObject.terraceDesignStations[index];
      addFunction({}, station);
    }
    this.stopTerraceSketch();
  }

  private handleTerraceEscKey = (event) => {
    if (event.key === 'Escape') {
      this.stopTerraceSketch();
    }
  };

  private setUpMultipoint(type: string) {
    if (this._profileView) {
      const multipoint: Multipoint = new Multipoint({ spatialReference: this._profileView.spatialReference });
      const length = terraceDesignStore.terraceDesignObject.terraceDesignStations.length;
      for (let i = 0; i < length; i++) {
        const station = terraceDesignStore.terraceDesignObject.terraceDesignStations[i];
        if (type === 'match' || (!station.blockStation && !station.drainStation)) {
          if ((type === 'drain' && (i === 0 || i === length - 1)) || (type === 'match' && (i !== 0 && i !== length - 1))) {
            continue;
          }
          const formattedStation = (parseFloat(station.endStation.replace("+", "")));
          const point = new Point({ x: formattedStation * designStore.commonHorizontalStretchFactor!, y: station.channelElev * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor), spatialReference: this._profileView.spatialReference });
          multipoint.addPoint(point);
          this.channelIndexes.push({ point, index: i });
        }
      }
      this.channelStationMP = multipoint;
      const graphic = new Graphic({ geometry: multipoint, symbol: reachStationSymbol.editableStationSymbol });
      const lyr = new GraphicsLayer({
        graphics: [graphic],
        id: 'editableStations'
      })
      this._profileView.map.addMany([lyr, this.feedbackLayer]);
      this._profileView.map.add(this.feedbackLayer);
    }
  }

  displayMatchedVertex(vertex) {
    const xStretchFactor = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
    const x = vertex.sourceAlignmentDistance * xStretchFactor;
    const y = vertex.channelElevation * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);

    const point = new Point({
      x: x,
      y: y,
      spatialReference: this._profileView?.spatialReference
    });

    const lyr = new GraphicsLayer({
      graphics: [new Graphic({ geometry: point, symbol: trackerSymbol })],
      id: 'selectedVertexProfile'
    });
    this._profileView?.map.add(lyr);
  }

  removeMatchedVertexLayers(mapView) {
    const profileLyr = this._profileView?.map.findLayerById('selectedVertexProfile');
    if (profileLyr) {
      this._profileView?.map.remove(profileLyr);
      profileLyr.destroy();
    }
    const mapLyr = mapView?.map.findLayerById('selectedVertexMap');
    if (mapView && mapLyr) {
      mapView.map.remove(mapLyr);
      mapLyr.destroy();
    }
  }

  protected get stationHitTestOptions(): __esri.MapViewHitTestOptions | undefined {
    const hitLayer = this._profileView?.map.findLayerById('reachStations');
    if (hitLayer) {
      const options: __esri.MapViewHitTestOptions = {};
      options.include = hitLayer;
      return options;
    }
    return undefined;
  }

  private get profileTrackLayer(): GraphicsLayer | undefined {
    const id = 'highlight_from_table_lyr';
    const title = 'highlight from table';

    if (!this._profileTrackLayer) {
      const lyr = this._profileView?.map.findLayerById(id);
      if (lyr) {
        this._profileTrackLayer = lyr as GraphicsLayer;
      } else {
        this._profileTrackLayer = new GraphicsLayer({ id, title, listMode: 'hide' });
        this._profileView?.map.add(this._profileTrackLayer);
      }
    }

    return this._profileTrackLayer;
  }

  public trackStation(station: any) {
    if (!(station.reachId === "START")) {
      if (!terraceDesignStore.checkedRowStations.includes(station.reachId)) {
        // Only proceed with Point creation if station has necessary properties
        if (station.endStation && station.channelElev) {
          const stationX = designStore.commonHorizontalStretchFactor
            ? (parseFloat(station.endStation.replace('+', '')) * designStore.commonHorizontalStretchFactor)
            : parseFloat(station.endStation.replace('+', ''));
          const stationY = designStore.commonVerticalStretchFactor
            ? station.channelElev * designStore.commonVerticalStretchFactor
            : station.channelElev * AppConstants.profileStretchFactor;
          const point = new Point({
            x: stationX,
            y: stationY,
            spatialReference: this._profileView?.spatialReference
          });
          this.trackProfilePoint(point, station.reachId);
        }
      }
    }
  }

  public trackProfilePoint(point: Point, id: string) {
    if (!this.profileTrackLayer?.graphics.toArray().some(graphic => {
      return (graphic.attributes && graphic.attributes.id && (graphic.attributes.id === id));
    })) {
      const graphic = new Graphic({
        geometry: point,
        symbol: reachHighlightSymbol,
        attributes: { id: id }
      });
      // console.warn(`ADDING GRAPHIC ${graphic.attributes.id}`);
      this.profileTrackLayer?.graphics.push(graphic);
    }
  }

  public unTrackProfilePoint(id: string) {
    // console.warn(`UNTRACK: ${id}`);
    const graphicToRemove = this.profileTrackLayer?.graphics.find(graphic => {
      return (graphic.attributes && graphic.attributes.id && (graphic.attributes.id === id));
    });
    if (graphicToRemove) {
      this.profileTrackLayer?.graphics.remove(graphicToRemove);
    }
  }

  public unTrackUnselectedProfilePoints() {
    const idsToDelete = this.profileTrackLayer?.graphics.toArray().filter(g => {
      return (
        g.attributes &&
        g.attributes.id &&
        !terraceDesignStore.checkedRowStations.some(s => s === g.attributes.id)
      );
    }).map(g => { return g.attributes.id; });
    if (idsToDelete) {
      for (const id of idsToDelete) {
        this.unTrackProfilePoint(id);
      }
    }
  }

  cleanUp() {
    this.viewPointerMoveHandle?.remove();
    this.viewPointerLeaveHandle?.remove();
    this.viewPointerNotInitialized = true;
    // this.removeSketch();
    if (this._profileTrackLayer) {
      this._profileView?.map?.remove(this._profileTrackLayer);
      this._profileTrackLayer.destroy();
      this._profileTrackLayer = void 0;
    }
  }

  Dispose() {
    this.cleanUp();
    super.Dispose();
    // this.mainSketchVm?.destroy();
  }

}

export { DynamicTerraceProfileStore };
